import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api as apiClient, uploadAudio } from "../api/client";

// App-level upload manager: a single queue that lives ABOVE the router, so
// uploads keep running when you navigate between projects (and you can upload to
// several at once). Each project page enqueues files here and reads progress
// back — it no longer owns the upload loop, so unmounting a page can't kill it.

const MAX_CONCURRENT = 3; // global cap so a big batch doesn't saturate the line
const AUDIO_RE = /\.(wav|mp3|m4a|flac|ogg|webm)$/i;

export type UploadStatus = "queued" | "uploading" | "done" | "error" | "canceled";

export interface UploadItem {
  id: string;
  projectId: string;
  projectName?: string;
  filename: string;
  size: number;
  status: UploadStatus;
  pct: number;
  error?: string;
}

// Internal record keeps the File + AbortController out of React state.
interface Rec extends UploadItem {
  file: File;
  ac?: AbortController;
  autoProof: boolean;
}

interface UploadsApi {
  items: UploadItem[];
  /** Returns how many files were newly queued (audio-only, de-duped vs in-flight).
   *  `autoProof` decides per file: true → start transcribe→map→proof the moment
   *  it lands; false → register only, for the user to choose a service later.
   *  A predicate is how a single drop can do both, which is the whole point of
   *  asking before the upload rather than after it. */
  enqueue: (projectId: string, files: FileList | File[], projectName?: string,
            autoProof?: boolean | ((file: File) => boolean)) => number;
  cancel: (id: string) => void;
  retry: (id: string) => void;
  clearFinished: () => void;
  activeCount: number;
  /** Arm/disarm "email me when this project's uploads finish". When armed, the
   *  manager fires a one-off backend notify the moment the project's last active
   *  upload completes (survives navigating between projects). */
  setNotifyOnDone: (projectId: string, projectName: string | undefined, on: boolean) => void;
  /** Project ids currently armed for an upload-complete email. */
  notifyArmed: string[];
}

const Ctx = createContext<UploadsApi | null>(null);

let _seq = 0;
const newId = () => `up_${Date.now().toString(36)}_${(_seq++).toString(36)}`;
const toPublic = (r: Rec): UploadItem => {
  const { file, ac, ...pub } = r; void file; void ac;
  return pub;
};
const isActive = (s: UploadStatus) => s === "queued" || s === "uploading";

export function UploadProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const store = useRef<Map<string, Rec>>(new Map());
  const running = useRef(0);
  // Projects armed for an "email me when uploads finish" notice → their name.
  const notify = useRef<Map<string, string | undefined>>(new Map());
  const [tick, bump] = useState(0);
  const sync = useCallback(() => bump((n) => n + 1), []);

  // Start queued items up to the concurrency cap. Safe to call repeatedly.
  const pump = useCallback(() => {
    for (const rec of store.current.values()) {
      if (running.current >= MAX_CONCURRENT) break;
      if (rec.status !== "queued") continue;
      rec.status = "uploading";
      rec.pct = 0;
      running.current += 1;
      const ac = new AbortController();
      rec.ac = ac;
      uploadAudio(
        rec.projectId,
        rec.file,
        (pct) => { if (pct !== rec.pct) { rec.pct = pct; sync(); } },
        ac.signal,
        rec.autoProof,
      )
        .then(() => {
          rec.status = "done";
          rec.pct = 100;
          // The take is on the server now — refresh the project's views so it
          // appears (and the auto-transcribe worker has already been kicked).
          qc.invalidateQueries({ queryKey: ["audio", rec.projectId] });
          qc.invalidateQueries({ queryKey: ["project", rec.projectId] });
          qc.invalidateQueries({ queryKey: ["projects"] });
          // Auto-dismiss the success row after a moment so the tray stays tidy
          // (the take now lives in the project's list). Errors/cancels persist.
          setTimeout(() => {
            if (store.current.get(rec.id)?.status === "done") { store.current.delete(rec.id); sync(); }
          }, 6000);
        })
        .catch((e: unknown) => {
          rec.status = ac.signal.aborted ? "canceled" : "error";
          rec.error = ac.signal.aborted ? undefined : ((e as Error)?.message || "Upload failed");
        })
        .finally(() => {
          rec.ac = undefined;
          running.current -= 1;
          sync();
          pump();
          // "Email me when done": once this project's uploads have all drained,
          // fire the backend notify exactly once (only if at least one actually
          // succeeded — an all-cancelled batch just disarms, no email).
          const pid = rec.projectId;
          if (notify.current.has(pid)) {
            const mine = [...store.current.values()].filter((r) => r.projectId === pid);
            if (!mine.some((r) => isActive(r.status))) {
              const done = mine.filter((r) => r.status === "done").length;
              notify.current.delete(pid);
              sync();
              if (done > 0) {
                apiClient(`/api/projects/${pid}/notify-upload-complete`, {
                  method: "POST", body: JSON.stringify({ count: done }),
                }).catch(() => {});
              }
            }
          }
        });
    }
    sync();
  }, [qc, sync]);

  const enqueue = useCallback((projectId: string, files: FileList | File[], projectName?: string,
                               autoProof: boolean | ((file: File) => boolean) = true) => {
    const wants = typeof autoProof === "function" ? autoProof : () => autoProof;
    let added = 0;
    for (const file of Array.from(files)) {
      if (!AUDIO_RE.test(file.name)) continue;
      const dup = [...store.current.values()].some(
        (r) => r.projectId === projectId && r.filename === file.name && isActive(r.status),
      );
      if (dup) continue;
      const id = newId();
      store.current.set(id, {
        id, projectId, projectName, filename: file.name, size: file.size,
        status: "queued", pct: 0, file, autoProof: wants(file),
      });
      added += 1;
    }
    if (added) { sync(); pump(); }
    return added;
  }, [pump, sync]);

  const cancel = useCallback((id: string) => {
    const rec = store.current.get(id);
    if (!rec) return;
    rec.ac?.abort();
    if (rec.status === "queued") rec.status = "canceled";
    sync();
  }, [sync]);

  const retry = useCallback((id: string) => {
    const rec = store.current.get(id);
    if (!rec || (rec.status !== "error" && rec.status !== "canceled")) return;
    rec.status = "queued";
    rec.pct = 0;
    rec.error = undefined;
    sync();
    pump();
  }, [pump, sync]);

  const clearFinished = useCallback(() => {
    for (const [id, rec] of [...store.current.entries()]) {
      if (rec.status === "done" || rec.status === "canceled") store.current.delete(id);
    }
    sync();
  }, [sync]);

  const setNotifyOnDone = useCallback((projectId: string, projectName: string | undefined, on: boolean) => {
    if (on) notify.current.set(projectId, projectName);
    else notify.current.delete(projectId);
    sync();
  }, [sync]);

  const items = useMemo(() => [...store.current.values()].map(toPublic), [tick]);
  const activeCount = items.filter((it) => isActive(it.status)).length;
  const notifyArmed = useMemo(() => [...notify.current.keys()], [tick]);

  // Warn before closing the tab while uploads are still running (they're
  // browser-direct, so a close/reload aborts them).
  useEffect(() => {
    if (activeCount === 0) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [activeCount]);

  const api: UploadsApi = { items, enqueue, cancel, retry, clearFinished, activeCount, setNotifyOnDone, notifyArmed };
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useUploads(): UploadsApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useUploads must be used within <UploadProvider>");
  return ctx;
}
